// =============================================================================
// useSchemaBackfill — stan bulk dogenerowania schemy (per mapa).
// Wzorzec z useStandaloneBulk: refy pause/stop + state running/progress/summary
// + beforeunload guard.
// =============================================================================
import { useCallback, useEffect, useRef, useState } from "react";
import {
  runSchemaBackfill,
  type SchemaBackfillItem,
  type SchemaBackfillControl,
  type SchemaBackfillProgress,
  type SchemaBackfillSummary,
} from "@/services/schema/schemaBackfillRunner";
import type { WpCreds } from "@/services/schema/schemaBackfillService";

const INITIAL: SchemaBackfillProgress = {
  current: 0, total: 0, ok: 0, skipped: 0, failed: 0, activeLabels: [], phase: "running",
};

export function useSchemaBackfill() {
  const [running, setRunning] = useState(false);
  const [progress, setProgress] = useState<SchemaBackfillProgress>(INITIAL);
  const [summary, setSummary] = useState<SchemaBackfillSummary | null>(null);
  const pauseRef = useRef(false);
  const stopRef = useRef(false);

  useEffect(() => {
    if (!running) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = "Trwa dogenerowanie schemy — zamknąć kartę?";
      return e.returnValue;
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [running]);

  const start = useCallback(
    async (items: SchemaBackfillItem[], creds: WpCreds): Promise<void> => {
      if (!items.length || running) return;
      setRunning(true);
      pauseRef.current = false;
      stopRef.current = false;
      setSummary(null);
      setProgress({ ...INITIAL, total: items.length });
      const control: SchemaBackfillControl = {
        isPaused: () => pauseRef.current,
        isStopRequested: () => stopRef.current,
      };
      try {
        const result = await runSchemaBackfill(items, creds, control, setProgress, Date.now());
        setSummary(result);
      } finally {
        setRunning(false);
        pauseRef.current = false;
        stopRef.current = false;
      }
    },
    [running],
  );

  const pause = useCallback(() => { if (running) { pauseRef.current = true; setProgress((p) => ({ ...p, phase: "paused" })); } }, [running]);
  const resume = useCallback(() => { if (running) { pauseRef.current = false; setProgress((p) => ({ ...p, phase: "running" })); } }, [running]);
  const stop = useCallback(() => { if (running) { stopRef.current = true; setProgress((p) => ({ ...p, phase: "stopping" })); } }, [running]);

  return { running, progress, summary, start, pause, resume, stop };
}
